import autobind from "autobind-decorator";
import type { ReactNode } from "react";
import type { LeadRequest } from "@/component/assets/AbstractLeadForm/types";
import { JsonDataBuilder } from "@/shared/lib/JsonDataBuilder";
import { logError } from "@/shared/lib/sentry-error";
import { formatText } from "@/shared/ui/format";
import type {
	SmeLoanLeadFormFields as LeadFormFormFields,
	SmeLoanLeadFormProps as LeadFormProps,
	SmeLoanLeadFormState as LeadFormState,
} from "../LeadForm/SmeLoanLeadForm";
import { SmeLoanLeadForm } from "../LeadForm/SmeLoanLeadForm";
import type { CompanyLegalForm, LoanGoal, SmeLoanLeadCalcRequest, SmeLoanLeadCalcResponse } from "../LeadForm/types";
import { calculateSmeLoan, getCompanyLegalFormGroup, getKoList, getLeadOtherData } from "./domain";
import * as Step from "./Steps";
import { EQUITY_LIMIT } from "./Steps/Step5.data";
import { LOAN_GOALS } from "./Steps/Step6.data";
import "./style.scss";

export interface FormFields extends LeadFormFormFields {
	// Step1
	companyFoundYear: number;
	companyLegalForm: CompanyLegalForm;

	// StepA2
	hasLessThanOneClosedYear: boolean; // StepB2
	negativeLastClosedBusinessYear: boolean;
	negativeEquity: boolean;
	hasOverdueNavDebt: boolean; // StepB2
	hasOverdueLoanDebt: boolean; // StepB2

	// StepA3
	hasAnotherCompanyUnderLiquidation: boolean;
	hasConnectedCompanyUnderLiquidation: boolean;
	ownerInKhr: boolean; // StepB2
	ownerDenied: boolean;
	hadMoreThanHalfOwnerChange: boolean;

	// StepB2
	hasNegativeIncomeAfterTaxation: boolean;

	// Step4
	hadNavCollection: boolean;
	hasRescheduledPublicDebt: boolean;

	// Step5
	netIncome: number;
	operatingProfit: number;
	depreciation: number;
	equity: number;

	// Step6
	loanGoal: LoanGoal;
	loanAmount: number;
	companyMainActivity: string;
	rentalYearly: number;
}

type Props = LeadFormProps<SmeLoanLeadCalcRequest, SmeLoanLeadCalcResponse>;

interface State<TFormFields extends FormFields = FormFields> extends LeadFormState<TFormFields> {
	koList: ReactNode[];
	result: number;
}

/**
 * @Shortcode
 *
 * @title "Mennyi hitelt kaphat a cégem kalkulátor"
 * @shortcode "kkv_small"
 * @slug "mennyi-hitelt-kaphat-a-cegem"
 * @type "calculator"
 */
export class SmeLoanHowMuchCreditSmallCalculator extends SmeLoanLeadForm<
	SmeLoanLeadCalcRequest,
	SmeLoanLeadCalcResponse,
	FormFields,
	Props,
	State
> {
	public static defaultProps = {
		title: "Add meg további adataidat és részletesen tájékoztatunk!",
		outsideTitle: "Mennyi hitelt kaphat céged?",
		outsideTitleLink: "/vallalati/hitel/mennyi-hitelt-kaphat-a-cegem/",
		bottomLogo: true,
	};

	protected getInitialState(): State {
		return {
			...super.getInitialState(),
			activeStep: "step-1",
			koList: [],
		};
	}

	protected renderSteps(): ReactNode {
		const { submitState, result, koList } = this.state;

		return (
			<>
				{super.renderSteps()}
				<Step.Step1 stepName="step-1" defaultTarget="step-2" bottomLogo />
				<Step.StepA2 stepName="step-a-2" defaultTarget="step-a-3" canStepBack />
				<Step.StepA3 stepName="step-a-3" defaultTarget="step-4" canStepBack />
				<Step.StepB2 stepName="step-b-2" defaultTarget="step-4" canStepBack />
				<Step.Step4 stepName="step-4" defaultTarget="step-5" canStepBack />
				<Step.Step5 stepName="step-5" defaultTarget="step-6" canStepBack />
				<Step.Step6 stepName="step-6" defaultTarget="result" canStepBack submitState={submitState} />
				<Step.Result stepName="result" defaultTarget="lead-form" result={result} />
				<Step.StepKo stepName="step-ko" defaultTarget="ko-check" canStepBack koList={koList} />
			</>
		);
	}

	protected onStep(target: string, withEvent: boolean): void {
		switch (target) {
			case "step-2":
				this.step1Check();
				break;
			case "step-4":
			case "step-6":
				this.checkForKo(target);
				break;
			case "result":
				this.calculate();
				break;
			default:
				super.onStep(target, withEvent);
		}
	}

	@autobind
	private step1Check(): void {
		const {
			values: { companyLegalForm },
		} = this.state;
		const legalFormGroup = getCompanyLegalFormGroup(companyLegalForm);

		super.onStep(`step-${legalFormGroup}-2`, true);
	}

	@autobind
	private calculate() {
		const { values } = this.state;
		this.setState({
			submitState: "loading",
		});

		this.promisePool
			.add(calculateSmeLoan(values))
			.then((response: number) => {
				this.setState({
					result: response,
					submitState: "done",
				});

				if (response > 0) {
					super.onStep("result", true);
				}
			})
			.catch((error: Error) => {
				this.setState({ submitState: "error" });

				logError(error);
			})
			.finally(() => {
				this.promisePool.addTimeout().then(() => {
					this.setState({ submitState: undefined });
				});
			});
	}

	@autobind
	protected getLeadData(): LeadRequest<SmeLoanLeadCalcRequest, SmeLoanLeadCalcResponse> {
		const {
			values,
			values: {
				companyLegalForm,
				companyMainActivity,
				companyFoundYear,
				loanAmount,
				netIncome,
				loanGoal,
				depreciation,
				equity,
				operatingProfit,
				ownerInKhr,
				negativeEquity,
				hasOverdueLoanDebt,
				hasOverdueNavDebt,
				hasAnotherCompanyUnderLiquidation,
				negativeLastClosedBusinessYear,
				rentalYearly,
			},
			result,
		} = this.state;
		const data = super.getLeadData();

		const otherData = getLeadOtherData(values, new JsonDataBuilder(data.request.otherDataJson));
		otherData.addItem("Elérhető hitelösszeg", "result", result, "ft");
		otherData.addItem("Bérelt ingatlan éves díja", "rentalYearly", rentalYearly || "0", "ft");
		otherData.addItem("Hitelcél", "loanGoal", LOAN_GOALS.find((lg) => lg.value === loanGoal).label);
		otherData.addItem("Értékcsökkenés", "depreciation", depreciation);
		otherData.addItem("Saját tőke", "equity", equity);
		otherData.addItem("Üzemi eredmény", "operatingProfit", operatingProfit);

		data.request = {
			...data.request,
			companyLegalForm,
			companyMainActivity,
			companyFoundYear,
			loanAmount,
			netIncome,
			loanGoal,
			depreciation,
			equity,
			operatingProfit,
			ownerInKhr,
			negativeEquity,
			hasOverdueLoanDebt,
			hasOverdueNavDebt,
			ownerHasAnotherCompanyUnderLiquidation: hasAnotherCompanyUnderLiquidation,
			positiveLastClosedBussinessYear: !negativeLastClosedBusinessYear,
			otherDataJson: otherData.getJson(),
		};

		return data;
	}

	@autobind
	private checkForKo(target: string): void {
		const {
			values,
			values: { companyLegalForm, equity },
		} = this.state;
		const koList = getKoList(values);

		if (companyLegalForm in EQUITY_LIMIT && equity < EQUITY_LIMIT[companyLegalForm]) {
			koList.push(
				"Sajnáljuk, de céged saját tőkéje, nem éri el a jegyzett tőke törvényi minimumát, így vállalkozásod nem hitelképes.",
			);
		}

		this.setState({
			koList,
		});

		super.onStep(koList.length ? "step-ko" : target, true);

		if (koList.length) {
			this.sendCalculationEvent(koList);
		}
	}

	protected getAutoresponderFields(): Record<string, any> {
		const {
			values: { lastName, firstName, loanGoal },
			result,
		} = this.state;

		return {
			lastName,
			firstName,
			loanGoal: LOAN_GOALS.find((goal) => goal.value === loanGoal).label,
			result: formatText(result, "ft"),
		};
	}

	protected getClassName(): string {
		return `${super.getClassName()} how-much-credit`;
	}

	protected getComponentName(): string {
		return `${super.getComponentName()}.SmeLoanHowMuchCreditSmallCalculator`;
	}
}

export default SmeLoanHowMuchCreditSmallCalculator;
